import { useState } from 'react'

import { FetchPolicy, TypedDocumentNode, useApolloClient } from '@apollo/client'
import { FiltersDefinition, RecordType } from '@factorialco/f0-react'
import { ResultOf, VariablesOf } from '@graphql-typed-document-node/core'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'

import { DEFAULT_PAGE_SIZE, PaginationVars } from './useGraphqlDataSource'

interface UseGraphqlChildrenDataCollectionProps<
  Record extends RecordType,
  Filters extends FiltersDefinition,
  Document extends TypedDocumentNode<ResultOf<Document>, VariablesOf<Document>>
> {
  query: Document
  mapVariables: (params: {
    item: Record
    filters?: Filters
    pagination?: PaginationInfo
  }) => Omit<VariablesOf<Document>, keyof PaginationVars>
  mapData: (params: { data: ResultOf<Document>; item: Record }) => Array<Record>
  totalCount: (data: ResultOf<Document>) => number
  fetchPolicy?: FetchPolicy
  perPage?: number
  childrenType?: 'detailed' | 'basic'
}

export type ChildrenPaginationInfo = {
  total: number
  perPage: number
  currentPage: number
  pagesCount: number
  hasMore: boolean
}

export interface PaginationInfo {
  currentPage: number
  perPage: number
}

interface FetchChildrenParams<Record extends RecordType, Filters extends FiltersDefinition> {
  item: Record
  filters?: Filters
  pagination?: PaginationInfo
}

export const useGraphqlChildrenDataCollection = <
  Document extends TypedDocumentNode<ResultOf<Document>, VariablesOf<Document>>,
  Record extends RecordType,
  Filters extends FiltersDefinition
>({
  query,
  mapVariables,
  mapData,
  totalCount,
  childrenType,
  fetchPolicy = 'network-only',
  perPage = DEFAULT_PAGE_SIZE,
}: UseGraphqlChildrenDataCollectionProps<Record, Filters, Document>) => {
  const client = useApolloClient()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const fetchChildren = useUnsafeCallback(
    async ({ item, filters, pagination }: FetchChildrenParams<Record, Filters>) => {
      setError(null)
      setLoading(true)

      try {
        const paginationInfo = pagination
          ? {
              perPage: pagination.perPage,
              currentPage: pagination.currentPage + 1,
            }
          : {
              perPage: perPage,
              currentPage: 1,
            }

        const paginationVars: {
          perPage: number
          page?: number
        } = {
          perPage: paginationInfo.perPage ?? perPage,
          page: paginationInfo.currentPage ?? 1,
        }

        const result = await client.query({
          query,
          fetchPolicy,
          variables: {
            ...mapVariables({ item, filters }),
            ...paginationVars,
          },
        })

        const mappedData = mapData({ data: result.data, item })
        const total = totalCount(result.data)

        setLoading(false)

        return {
          records: mappedData,
          type: childrenType ?? 'basic',
          paginationInfo: {
            cursor: null,
            total: total,
            perPage: perPage,
            currentPage: paginationInfo.currentPage,
            pagesCount: Math.ceil(total / perPage),
            hasMore: paginationInfo.currentPage * paginationInfo.perPage < total,
          },
        }
      } catch (err) {
        const caughtError = err instanceof Error ? err : new Error(String(err))
        setError(caughtError)
        setLoading(false)
        throw caughtError
      }
    },
    [client, query, mapVariables, mapData, totalCount, fetchPolicy, perPage, childrenType]
  )

  return {
    fetchChildren,
    loading,
    error,
  }
}
